import { PayloadAction } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { update } from 'core/actions/admin/data';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { selectAction, selectActionDAO } from 'core/selectors/product/action/action';
import { selectScene } from 'core/selectors/product/scene/scene';
import { batchRequest, createRequest } from 'core/services/admin';
import { uploadRequest } from 'core/services/asset';
import { IUploadFile } from 'core/slices/upload';
import { all, call, fork, put, select, takeLatest, take } from 'redux-saga/effects';
import { RootState } from 'types';
import { IAction, IActionBasic, IActionDAO } from 'types/domain/action';
import {
  ISceneConfig,
  ISceneConfigDAO,
  ISceneDAO,
  IScenePreview,
  SceneConfigFit,
  SceneProjection,
  SceneType,
} from 'types/domain/scene';
import { ICopySettings, ISceneEditorState, sceneEditorActions } from '.';
import { ICameraState } from 'core/slices/player';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import { selectSceneConfig } from 'core/selectors/product/scene/sceneConfig';
import { selectSceneEditor } from './selectSceneEditor';
import { IEditState, actionEditorActions } from 'app/components/ActionEdit/slice';
import { selectPortrait } from 'core/selectors/product/portrait';
import { errorRequest } from 'core/services/product';
import { IError } from 'types/app/error';

function* previewCapture(action: PayloadAction<IScenePreview>) {
  const offlineId = yield select(selectOfflineId);
  const folderId = yield select((state: RootState) => state.state[EntityTypes.FOLDER]);
  let wait = true;

  while (wait) {
    const createResponse: PayloadAction<any> = yield take(createRequest.fulfilled.type);
    if (createResponse.payload.offlineId === offlineId) {
      wait = false;
      yield put(
        yield call(uploadRequest, {
          ref: action.payload.url,
          id: createResponse.payload.id,
          folderId: createResponse.payload.folderId || folderId,
          signedUrl: createResponse.payload.signedUrl,
          contentType: createResponse.payload.contentType,
        } as IUploadFile),
      );
    }
  }
}

function* actionEditSaga(action: PayloadAction<IEditState>) {
  // const actionDAO: IActionDAO = yield select(state => selectActionDAO(state, undefined));;
  // const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
}

function* pasteSceneSaga(action: PayloadAction<number>) {
  const copy: ICopySettings = yield select((state: RootState) => state.sceneEditor?.copy);

  const origin: IAction = yield select((state: RootState) =>
    //@ts-ignore
    selectAction(state, state.sceneEditor.copy.actionId),
  );
  //@ts-ignore
  const target: IAction = yield select((state: RootState) => selectAction(state, action.payload));

  yield put(yield call(sceneEditorActions.copySceneSettings, null));
  if (copy.positionId) {
    //@ts-ignore
    const originConfig = yield select((state: RootState) => selectSceneConfig(state, origin.sceneId, origin.id));

    //@ts-ignore
    const targetConfig = yield select((state: RootState) => selectSceneConfig(state, target.sceneId, target.id));

    if (!originConfig || !targetConfig)
      return yield put(
        call(errorRequest, {
          message: 'Could not copy settings. An error occured.',
          code: 0,
          stackTrace: JSON.stringify({ copy, payload: action.payload }),
        } as IError),
      );

    const offlineId = yield select(selectOfflineId);
    const position = yield select(
      (state: RootState) => state.entity.present[EntityTypes.POSITION][originConfig.positionId],
    );
    const pPosition = yield select(
      (state: RootState) => state.entity.present[EntityTypes.POSITION][originConfig.pPositionId],
    );
    const pOfflineId = position && pPosition ? offlineId + 1 : offlineId;
    if (position)
      yield put(
        yield call(batchRequest, {
          batch: [
            ...(position
              ? [
                  {
                    type: EntityTypes.POSITION,
                    parentEntityType: EntityTypes.SCENE_CONFIG,
                    parentEntityId: targetConfig.id,
                    parentModuleId: target.moduleId,
                    parentEntityAttribute: 'positionId',
                    mergeData: true,
                    entity: { ...position, id: offlineId, offlineId },
                  },
                ]
              : []),
            ...(pPosition
              ? [
                  {
                    type: EntityTypes.POSITION,
                    parentEntityType: EntityTypes.SCENE_CONFIG,
                    parentEntityId: targetConfig.id,
                    parentModuleId: target.moduleId,
                    parentEntityAttribute: 'pPositionId',
                    mergeData: true,
                    entity: { ...pPosition, id: pOfflineId, offlineId: pOfflineId },
                  },
                ]
              : []),
          ],
        }),
      );
  } else
    yield put(
      yield call(update, {
        type: EntityTypes.ACTION,
        parentEntityType: EntityTypes.ACTION,
        parentEntityId: origin.id,
        parentModuleId: origin.moduleId,
        mergeData: true,
        entity: { id: target.id, sceneId: origin.sceneId },
      }),
    );
}

function* setCameraDefault() {
  const mode: Mode = yield select(selectSessionMode);
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const sceneEdior: ISceneEditorState = yield select(selectSceneEditor);
  const { portrait, camera } = yield select((state: RootState) => state.core.player.state);
  const fit = sceneConfig ? (portrait ? sceneConfig.pFit : sceneConfig.fit) : null;

  if (mode !== Mode.ADMIN) return;

  const position = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
  const rotation = { x: camera.rotation.x, y: camera.rotation.y, z: camera.rotation.z };

  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  const offlineId = yield select(selectOfflineId);
  const sceneConfigId = action && action.sceneConfigId ? action.sceneConfigId : scene ? scene.sceneConfigId : null;

  const positionId =
    action && action.sceneConfig
      ? portrait
        ? action.sceneConfig.pPositionId
        : action.sceneConfig.positionId
      : scene
      ? portrait
        ? scene.sceneConfig.pPositionId
        : scene.sceneConfig.positionId
      : null;
  const rotationId =
    action && action.sceneConfig
      ? portrait
        ? action.sceneConfig.pRotationId
        : action.sceneConfig.rotationId
      : scene
      ? portrait
        ? scene.sceneConfig.pRotationId
        : scene.sceneConfig.rotationId
      : null;

  if (scene && (scene.type === SceneType.MODEL || scene.projection === SceneProjection['3603D']))
    yield put(
      yield call(update, {
        type: EntityTypes.ROTATION,
        parentEntityType: EntityTypes.SCENE_CONFIG,
        parentEntityId: sceneConfigId,
        parentModuleId: action?.moduleId,
        parentEntityAttribute: portrait ? 'pRotationId' : 'rotationId',
        entity: {
          id: rotationId || offlineId,
          offlineId,
          ...rotation,
        },
      }),
    );
  yield put(
    yield call(update, {
      type: EntityTypes.POSITION,
      parentEntityType: EntityTypes.SCENE_CONFIG,
      parentEntityId: sceneConfigId,
      parentModuleId: action?.moduleId,
      parentEntityAttribute: portrait ? 'pPositionId' : 'positionId',
      entity: {
        id: positionId || offlineId,
        offlineId,
        ...position,
      },
    }),
  );

  if (sceneConfig && sceneConfig.fit !== SceneConfigFit.POSITION)
    yield put(
      yield call(update, {
        type: EntityTypes.SCENE_CONFIG,
        entity: portrait
          ? { id: sceneConfig.id, pFit: SceneConfigFit.POSITION }
          : { id: sceneConfig.id, fit: SceneConfigFit.POSITION },
      }),
    );
}

function* resetSaga() {
  const sceneConfig: ISceneConfigDAO = yield select(state => selectSceneConfig(state, undefined, undefined));
  const portrait = yield select(selectPortrait);
  const parentCourseId = yield select((state: RootState) => state.state[EntityTypes.COURSE]);

  if (
    (portrait && sceneConfig.fit !== SceneConfigFit.POSITION) ||
    (!portrait && sceneConfig.pFit !== SceneConfigFit.POSITION)
  )
    yield put(
      yield call(update, {
        type: EntityTypes.POSITION,
        parentEntityType: EntityTypes.SCENE_CONFIG,
        parentEntityId: sceneConfig.id,
        parentCourseId,
        entity: {
          id: portrait ? sceneConfig.pPositionId : sceneConfig.positionId,
          x: 0,
          y: 0,
          z: 0,
        },
      }),
    );
}

function* updateCameraStateSaga(action: PayloadAction<ICameraState>) {
  if (action.payload.save) yield setCameraDefault();
}

function* watchPreviewCapture() {
  yield takeLatest(sceneEditorActions.scenePreviewCaptured, previewCapture);
}

function* watchSetCameraDefault() {
  yield takeLatest(sceneEditorActions.setCameraDefault, setCameraDefault);
}

function* watchActionEditor() {
  yield takeLatest(actionEditorActions.edit, actionEditSaga);
}

function* watchPasteScene() {
  yield takeLatest(sceneEditorActions.pasteSceneSettings, pasteSceneSaga);
}

function* watchReset() {
  yield takeLatest(sceneEditorActions.reset, resetSaga);
}

function* watchUpdateCamera() {
  // yield debounce(3000, updateCameraState, updateCameraStateSaga);
}

export function* SceneEditorSaga() {
  yield all([
    fork(watchSetCameraDefault),
    fork(watchPreviewCapture),
    fork(watchPasteScene),
    fork(watchUpdateCamera),
    fork(watchActionEditor),
    fork(watchReset),
  ]);
}
